/**
 * Who is coming BACK to a trial (2026-10-02, docs/CONVERSION-DIAGNOSIS-2026-09-26.md "Follow-ups"). Two parents came back to
 * their free story (a bedtime reload of the old tab, a trial holder on the sample again) and both were counted as
 * new sample visitors. This module tells a return from the creation hand-off, per page load:
 *
 * - `markCreatedThisLoad` — lib/anonStory.createAnonStory records the token it just made (in memory only), so the
 *   SPA hand-off from the sample to the new story, and every back-and-forth in the same page load, is no return.
 * - the "seen" list — the own story's ids opened on this device before (localStorage, ids only, never sent). It is
 *   read ONCE per page load, so the page load that first opens a story (also the static /start/ hand-off, a full
 *   page load) does not count it as a return.
 * - `firstStatusThisLoad` — the status this page load first saw per story (in memory). A story that was still
 *   generating then is the creation visit: no return, no welcome-back card, even once it is a calendar day old.
 *
 * Pure parts (`ownReturn`, `welcomeBack`, `storyForToken`) are Jest-tested in trialReturn.test.ts.
 */

const SEEN_KEY = 'snugtale_trial_seen_v1';
const MAX_SEEN = 40;

const createdThisLoad = new Set<string>();
let seenAtLoad: Set<string> | null = null;
const sentThisLoad = new Set<string>();
const firstStatus = new Map<string, string>();

export function markCreatedThisLoad(shareToken: string | null | undefined): void {
  if (shareToken) createdThisLoad.add(shareToken);
}

export function wasCreatedThisLoad(shareToken: string | null | undefined): boolean {
  return !!shareToken && createdThisLoad.has(shareToken);
}

function readSeen(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(SEEN_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch { return []; }
}

/** Was this story opened as the device's own trial in an EARLIER page load? (snapshot taken on first use) */
export function seenBeforeThisLoad(storyId: string): boolean {
  if (!seenAtLoad) seenAtLoad = new Set(readSeen());
  return seenAtLoad.has(storyId);
}

/** Remember the own story as seen (for the next page loads; the snapshot of this one stays as it was). */
export function markSeen(storyId: string): void {
  if (!seenAtLoad) seenAtLoad = new Set(readSeen());
  try {
    const list = readSeen().filter((id) => id !== storyId);
    list.push(storyId);
    localStorage.setItem(SEEN_KEY, JSON.stringify(list.slice(-MAX_SEEN)));
  } catch { /* storage unavailable: no return beacon, nothing breaks */ }
}

/** True the first time per page load for `kind` — the return beacons are "once per page load". */
export function firstThisLoad(kind: string): boolean {
  if (sentThisLoad.has(kind)) return false;
  sentThisLoad.add(kind);
  return true;
}

/** The story's status as THIS page load first saw it (recorded on first call per story id, kept for the page load).
 *  A story that was still generating when the page load first showed it is the creation visit — also when it
 *  finishes after midnight, or after the static /start/ hand-off (a full page load that did not call createAnonStory). */
export function firstStatusThisLoad(storyId: string, status: string | null | undefined): string {
  const seen = firstStatus.get(storyId);
  if (seen !== undefined) return seen;
  const now = status || 'complete';
  firstStatus.set(storyId, now);
  return now;
}

interface ReturnFacts {
  /** this page load made the story (createAnonStory) */
  createdHere: boolean;
  /** this page load first saw the story already complete (`firstStatusThisLoad(…) === 'complete'`) */
  firstSeenComplete: boolean;
  /** an earlier page load showed it as the device's own trial */
  seenBefore: boolean;
  /** whole calendar days since the story was made */
  daysOld: number;
}

/** The own trial story opened again (return_own): not the page load that made it, first seen already complete in
 *  this page load, and either seen in an earlier page load (same day too) or made on an earlier calendar day. A story
 *  that was generating when this page load first saw it never counts, however old it is by now. */
export function ownReturn({ createdHere, firstSeenComplete, seenBefore, daysOld }: ReturnFacts): boolean {
  return !createdHere && firstSeenComplete && (seenBefore || daysOld >= 1);
}

/** The welcome-back card: a return (`ownReturn`) on a later calendar day — never the creation visit, never a
 *  same-day reload. */
export function welcomeBack(facts: Omit<ReturnFacts, 'seenBefore'>): boolean {
  return ownReturn({ ...facts, seenBefore: false });
}

/** The reader's token guard (2026-10-02): a story loaded for another token is never rendered under this one.
 *  Going back from the own story to the sample used to render the own story for one pass under the sample's
 *  token — the beacons flipped to h = 'sample' and fired page3/last/slide, and the A′ slide mounted. */
export function storyForToken<T>(loaded: { token: string; story: T } | null, token: string | undefined): T | null {
  return loaded && token !== undefined && loaded.token === token ? loaded.story : null;
}

/** Tests only. */
export function resetTrialReturnForTests(): void {
  createdThisLoad.clear();
  sentThisLoad.clear();
  firstStatus.clear();
  seenAtLoad = null;
}
